import Card from './Card'
import {createUseStyles} from 'react-jss'
import {Colors} from '../constants/colors'
import {formatDateString, formatDateTimeString} from '../utils/dateHelper'
import React, {useMemo, useState} from 'react'
import {OpenShiftModel} from '../redux/types'
import {
  ColumnDef,
  ExpandedState,
  Header,
  PaginationState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {formatMoney} from '../utils/moneyHelper'
import Pagination from './Pagination'
import {byDate} from '../utils/chronologicalSort'

interface Props {
  openShifts: OpenShiftModel[]
}

const OpenShiftList = ({openShifts}: Props) => {
  const styles = useStyles()
  const [expanded, setExpanded] = useState<ExpandedState>({})
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  })
  const [sorting, setSorting] = useState<SortingState>([{id: 'date', desc: true}])
  const notSpecified = 'Not specified'

  const columns = useMemo<ColumnDef<OpenShiftModel>[]>(() => [
    {
      id: 'date',
      header: 'Date',
      accessorFn: (row) => row.date,
      cell: ({row}) => <span>{formatDateString(row.original.date)}</span>,
      sortingFn: byDate((row) => row.original.date),
    },
    {
      id: 'hours',
      header: 'Hours',
      cell: ({row}) => <span>{row.original.hours}</span>,
      enableSorting: false,
    },
    {
      id: 'time',
      header: 'Time',
      cell: ({row}) => {
        const start = row.original.startTime ? formatDateTimeString(row.original.startTime) : '?'
        const end = row.original.endTime ? formatDateTimeString(row.original.endTime) : '?'
        return <span>{start} - {end}</span>
      },
      enableSorting: false,
    },
    {
      id: 'estimatedGrossWage',
      header: 'Est. Gross Wage',
      cell: ({row}) => (
        <span>
          {formatMoney(row.original.estimatedGrossWage)}
        </span>
      ),
      enableSorting: false,
    },
    {
      id: 'estimatedNetWage',
      header: 'Est. Net Wage',
      cell: ({row}) => (
        <span>
          {formatMoney(row.original.estimatedNetWage)}
        </span>
      ),
      enableSorting: false,
    },
  ], [])

  const table = useReactTable({
    data: openShifts ?? [],
    columns,
    state: {expanded, pagination, sorting},
    onExpandedChange: setExpanded,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowCanExpand: () => true,
  })

  const renderHeaderContent = (header: Header<OpenShiftModel, unknown>) => {
    if (header.isPlaceholder) return null

    const canSort = header.column.getCanSort()
    const sortDir = header.column.getIsSorted()

    if (!canSort) {
      return flexRender(header.column.columnDef.header, header.getContext())
    }

    return (
      <button
        type="button"
        className={styles.headerButton}
        onClick={header.column.getToggleSortingHandler()}
      >
        {flexRender(header.column.columnDef.header, header.getContext())}
        <span>
          {sortDir === 'asc' ? ' ▲' : sortDir === 'desc' ? ' ▼' : ''}
        </span>
      </button>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th key={header.id} className={styles.headerCell}>
                  {renderHeaderContent(header)}
                </th>
              ))}
            </tr>
          ))}
          </thead>
          <tbody>
          {table.getRowModel().rows.map(row => (
            <React.Fragment key={row.id}>
              <tr
                className={styles.bodyRow}
                onClick={() => row.toggleExpanded()}
                tabIndex={0}
              >
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id} className={styles.bodyCell}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
              {row.getIsExpanded() && (
                <tr>
                  <td className={styles.expandedCell} colSpan={Math.ceil(row.getVisibleCells().length)}>
                    <div className={styles.detailsContainer}>
                      <Card className={`${styles.detailsCard} card`}>
                        <h6>Details</h6>
                        <p>External Id: <span>{row.original.externalId || notSpecified}</span></p>
                        <p>Location 1: <span>{row.original.location1 || notSpecified}</span></p>
                        <p>Location 2: <span>{row.original.location2 || notSpecified}</span></p>
                        <p>Location 3: <span>{row.original.location3 || notSpecified}</span></p>
                        <p>Field 1: <span>{row.original.field1 || notSpecified}</span></p>
                        <p>Field 2: <span>{row.original.field2 || notSpecified}</span></p>
                        {row.original.externalUrl && (
                             <p>External URL: <a href={row.original.externalUrl} target="_blank" rel="noreferrer">{row.original.externalUrl}</a></p>
                        )}
                      </Card>
                      <Card className={`${styles.detailsCard} card`}>
                        <h6>Wage</h6>
                        <p>Gross Wages: <span>{formatMoney(row.original.grossWages)}</span></p>
                        <p>Est. Gross Wage: <span>{formatMoney(row.original.estimatedGrossWage)}</span></p>
                        <p>Est. Net Wage: <span>{formatMoney(row.original.estimatedNetWage)}</span></p>
                      </Card>
                      <Card className={`${styles.detailsCard} card`}>
                        <h6>Flags</h6>
                        <p>Is new: <span>{row.original.isNew ? 'yes' : 'no'}</span></p>
                        <p>Schedule conflict: <span>{row.original.scheduleConflict ? 'yes' : 'no'}</span></p>
                        <p>Resting time rule violation: <span>{row.original.restingTimeRuleViolation ? 'yes' : 'no'}</span></p>
                      </Card>
                      <Card className={`${styles.detailsCard} card`}>
                        <h6>Comment</h6>
                        <p style={{whiteSpace: 'pre-wrap'}}>{row.original.comment || notSpecified}</p>
                      </Card>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          </tbody>
        </table>
      </div>
      <Pagination
        itemCount={openShifts?.length ?? 0}
        pageIndex={pagination.pageIndex + 1}
        pageSize={pagination.pageSize}
        siblingCount={1}
        onPageChange={(page) => table.setPageIndex(page - 1)}
      />
    </div>
  )
}

export default OpenShiftList

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  tableContainer: {
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0 0px',
    fontSize: '14px',
  },
  headerCell: {
    padding: '6px 12px',
    textAlign: 'left',
    fontFamily: 'Aestetico-Bold'
  },
  headerButton: {
    all: 'unset',
    display: 'inline-flex',
    gap: 6,
    cursor: 'pointer',
  },
  bodyRow: {
    cursor: 'pointer',
    transition: '0.4s',
    '&:hover': {
      backgroundColor: Colors.darkGrayHover,
    },
  },
  bodyCell: {
    padding: '2px 12px',
    textAlign: 'left',
    '&:first-child': {
      borderTopLeftRadius: '6px',
      borderBottomLeftRadius: '6px',
    },
    '&:last-child': {
      borderTopRightRadius: '6px',
      borderBottomRightRadius: '6px',
    },
  },
  expandedCell: {
    paddingTop: 2,
    paddingBottom: 22,
    paddingLeft: 22,
    paddingRight: 2,
  },
  detailsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  detailsCard: {
    flex: '1 1 300px',
    padding: 8,
    borderRadius: 8,
    backgroundColor: Colors.grayCard
  },
})
